import { ReactElement, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import SearchIcon from '@mui/icons-material/Search';
import RotateRightIcon from '@mui/icons-material/RotateRight';

import { setEndOfDay } from '~/utils/date';
import { IHospitalListFilters, IHospitalSelectors } from '~/services/api/hospital/types';
import { AdtEventAction } from '~/services/api/enums';
import DatePicker from '~/ui/components/inputs/DatePicker';
import Input from '~/ui/components/inputs/Input';
import SelectAll from '~/ui/components/inputs/SelectAll';
import Button from '~/ui/components/common/Button';
import { getInitialValues, validate } from './form';

import styles from './Header.module.scss';

const eventOptions = [
  {
    value: AdtEventAction.Register,
    label: 'Registered',
  },
  {
    value: AdtEventAction.PreAdmit,
    label: 'Pre-Admitted',
  },
  {
    value: AdtEventAction.Admit,
    label: 'Admitted',
  },
  {
    value: AdtEventAction.Transfer,
    label: 'Transferred',
  },
  {
    value: AdtEventAction.Discharge,
    label: 'Discharged',
  },
  {
    value: AdtEventAction.CancelAdmit,
    label: 'Cancel Admit',
  },
];

interface IProps {
  selectors: IHospitalSelectors;
  isLoading: boolean;
  eventIdsPreset?: AdtEventAction[];
  onSubmit: (filters: IHospitalListFilters) => void;
}

const Header = ({ selectors, isLoading, eventIdsPreset, onSubmit }: IProps): ReactElement => {
  const initialValues = getInitialValues();

  const formMethods = useForm<IHospitalListFilters>({
    resolver: validate,
    defaultValues: initialValues,
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = formMethods;

  const programOptions = useMemo(
    () =>
      (selectors?.programs ?? []).map(item => ({
        value: item.id,
        label: item.name,
      })),
    [selectors?.programs],
  );

  const facilityOptions = useMemo(
    () =>
      (selectors?.facilities ?? []).map(item => ({
        value: item,
        label: item,
      })),
    [selectors?.facilities],
  );

  const onSubmitForm = (values: IHospitalListFilters) => {
    onSubmit({
      ...values,
      endDate: setEndOfDay(values.endDate),
    });
  };

  useEffect(() => {
    if (!eventIdsPreset) return;
    setValue('eventIds', eventIdsPreset);
  }, [eventIdsPreset, setValue]);

  return (
    <FormProvider {...formMethods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmitForm)}>
        <Grid container spacing={2}>
          <Grid item xs>
            <DatePicker
              placeholder=".. /.. /.."
              label="Start Date"
              name="startDate"
              maxDate={new Date()}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs>
            <DatePicker
              placeholder=".. /.. /.."
              label="End Date"
              name="endDate"
              maxDate={new Date()}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs>
            <Input
              name="patientFullName"
              register={register}
              errors={errors}
              label="Patient"
              placeholder="Patient"
            />
          </Grid>
          <Grid item xs>
            <SelectAll
              name="programIds"
              control={control}
              errors={errors}
              options={programOptions}
              label="Program"
              isLoading={isLoading}
              placeholder="All Programs"
            />
          </Grid>
          <Grid item xs>
            <SelectAll
              name="facilities"
              control={control}
              errors={errors}
              options={facilityOptions}
              label="Facility"
              isLoading={isLoading}
              placeholder="All Facilities"
            />
          </Grid>
          <Grid item xs>
            <SelectAll
              name="eventIds"
              control={control}
              errors={errors}
              options={eventOptions}
              label="Event"
              isLoading={isLoading}
              placeholder="All Events"
            />
          </Grid>
          <Grid item>
            <Button variant="outlined" type="submit" color="primary" startIcon={<SearchIcon />}>
              Search
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              type="button"
              startIcon={<RotateRightIcon />}
              onClick={() => {
                reset(initialValues);
                onSubmit(initialValues);
              }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default Header;
